export default class CoverSelector {
  constructor(container = null) {
    // Usar window.COVER_CONFIG si está disponible
    const coverConfig = window.COVER_CONFIG || {}
    this.covers = coverConfig.covers || []
    if (!this.covers.length) {
      console.warn('CoverSelector: No covers found in COVER_CONFIG')
      return
    }

    // Elementos del DOM
    this.paletteContainer = document.getElementById('cover-background-palette')
    this.bgInput = document.getElementById('cover-background-color')
    this.fontSelect = document.getElementById('cover-typography')
    
    // Elementos SVG de la previsualización
    this.svgCoverDetails = document.getElementById('svg-cover-details')
    this.leftCoverRect = document.getElementById('cover-left-sheet-background-rect')
    this.rightCoverRect = document.getElementById('cover-right-sheet-background-rect')
    this.spineRect = document.getElementById('cover-spine-background-rect')
    this.leftCoverTitleText = document.getElementById('cover-left-sheet-title-text')
    this.leftCoverSubtitleText = document.getElementById('cover-left-sheet-subtitle-text')
    this.spineText = document.getElementById('cover-spine-text')
    // La contraportada no tiene elementos de texto editables, solo el logo

    // Inputs de texto
    this.titleInput = document.getElementById('cover-title')
    this.subtitleInput = document.getElementById('cover-subtitle')
    this.spineInput = document.getElementById('cover-spine-text')

    // Configuración del fotobook para cálculos de tamaño
    this.fotobookConfig = window.FOTOBOOK_CONFIG || {}
    this.pageWidth = this.fotobookConfig.pageWidth || 300
    this.pageHeight = this.fotobookConfig.pageHeight || 300

    // Cover por defecto
    this.currentCoverId = coverConfig.defaultCoverId || this.covers[0]?.id

    // Inicializar
    if (this.currentCoverId) {
      this.selectCover(this.currentCoverId)
    }
    this.attachEvents()
  }

  attachEvents() {
    // Selección de color de fondo desde la paleta
    if (this.paletteContainer) {
      this.paletteContainer.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-color]')
        if (!chip) return
        const color = chip.getAttribute('data-color')
        this.setBackgroundColor(color)
        this.markActiveChip(color)
      })
    }

    // Cambio de tipografía
    if (this.fontSelect) {
      this.fontSelect.addEventListener('change', (e) => {
        const font = e.target.value
        this.applyFontToPreview(font)
      })
    }

    // Actualizar previsualización cuando cambia el título
    if (this.titleInput) {
      this.titleInput.addEventListener('input', (e) => {
        this.updateTitlePreview(e.target.value)
      })
    }

    // Actualizar previsualización cuando cambia el subtítulo
    if (this.subtitleInput) {
      this.subtitleInput.addEventListener('input', (e) => {
        this.updateSubtitlePreview(e.target.value)
      })
    }

    // Actualizar previsualización cuando cambia el texto del lomo
    if (this.spineInput) {
      this.spineInput.addEventListener('input', (e) => {
        this.updateSpinePreview(e.target.value)
      })
    }
  }

  selectCover(coverId) {
    const cover = this.covers.find(c => String(c.id) === String(coverId)) || this.covers[0]
    if (!cover) return
    this.currentCoverId = cover.id

    // Actualizar paleta y fuentes
    this.updatePalette(cover)
    this.updateFonts(cover)

    // Actualizar inputs con valores del cover
    if (this.titleInput && cover.title) {
      this.titleInput.value = cover.title
    }
    if (this.subtitleInput && cover.subtitle) {
      this.subtitleInput.value = cover.subtitle
    }

    // Actualizar previsualización completa del cover
    this.updateCoverPreview(cover)

    // Notificar a FotobookEditor si existe
    if (window.fotobookEditor && window.fotobookEditor.selectCoverFormat) {
      // Buscar el elemento del formato de cover correspondiente
      const coverFormatOption = document.querySelector(`[data-cover-id="${coverId}"].cover-format-option`)
      if (coverFormatOption) {
        window.fotobookEditor.selectCoverFormat(coverFormatOption)
      }
    }
  }

  updatePalette(cover) {
    const colors = this.parseColors(cover.palette)
    if (!this.paletteContainer) return

    this.paletteContainer.innerHTML = ''
    const palette = colors.length ? colors : ['#ffffff']

    palette.forEach((color, idx) => {
      const chip = document.createElement('button')
      chip.type = 'button'
      chip.className = 'palette-color-btn-fotobook'
      chip.style.background = this.hexToRgbCss(color)
      chip.style.color = this.getContrastColor(color)
      chip.style.padding = '.25em'
      chip.setAttribute('data-color', color)
      chip.setAttribute('title', color)

      const text = document.createElement('span')
      text.textContent = color;
      text.classList.add('sr-only');
      chip.appendChild(text);

      this.paletteContainer.appendChild(chip)

      if (idx === 0) {
        this.setBackgroundColor(color)
        chip.classList.add('active')
      }
    })
  }

  updateFonts(cover) {
    if (!this.fontSelect) return
    const fonts = this.parseFonts(cover.font_families)

    this.fontSelect.innerHTML = ''
    fonts.forEach((font, idx) => {
      const opt = document.createElement('option')
      opt.value = font
      opt.textContent = font
      opt.style.fontFamily = `'${font}', sans-serif`
      this.fontSelect.appendChild(opt)
      if (idx === 0) {
        this.fontSelect.value = font
        this.applyFontToPreview(font)
      }
    })
  }

  setBackgroundColor(color) {
    if (this.bgInput) {
      this.bgInput.value = color
    }
    // Actualizar rects de fondo en la previsualización
    if (this.leftCoverRect) {
      this.leftCoverRect.setAttribute('fill', color)
    }
    if (this.rightCoverRect) {
      this.rightCoverRect.setAttribute('fill', color)
    }
    // Actualizar color del lomo también
    if (this.spineRect) {
      this.spineRect.setAttribute('fill', color)
    }
  }

  applyFontToPreview(font) {
    const fontValue = font ? `'${font}', sans-serif` : null
    if (fontValue) {
      // Aplicar fuente a los elementos de texto editables
      const textElements = [
        this.leftCoverTitleText,
        this.leftCoverSubtitleText,
        this.spineText
      ]
      textElements.forEach(node => {
        if (node) node.setAttribute('font-family', fontValue)
      })
    }
  }

  updateCoverPreview(cover) {
    // Actualizar #svg-cover-details con la estructura completa del cover
    if (this.svgCoverDetails) {
      this.renderCoverDetails(cover)
    }

    // Asegurar que los elementos de texto existan antes de actualizarlos
    const contentGroup = this.svgCoverDetails?.querySelector('g.cover-sheet-content')
    if (contentGroup) {
      this.ensureTextElements(contentGroup)
    }

    // Actualizar título en la previsualización
    if (cover.title) {
      this.updateTitlePreview(cover.title)
    }

    // Actualizar subtítulo en la previsualización
    if (cover.subtitle) {
      this.updateSubtitlePreview(cover.subtitle)
    }

    // Actualizar color de fondo si hay paleta
    if (cover.palette) {
      const colors = this.parseColors(cover.palette)
      if (colors.length > 0) {
        this.setBackgroundColor(colors[0])
      }
    }

    // Actualizar posiciones y tamaños del texto según el cover
    this.updateTextPositions(cover)
  }

  renderCoverDetails(cover) {
    if (!this.svgCoverDetails) return

    // Obtener el grupo de contenido existente o crear uno nuevo
    let contentGroup = this.svgCoverDetails.querySelector('g.cover-sheet-content')
    if (!contentGroup) {
      contentGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
      contentGroup.setAttribute('class', 'cover-sheet-content')
      this.svgCoverDetails.appendChild(contentGroup)
    } else {
      // Limpiar solo las formas (slots de fotos), mantener los textos
      const existingShapes = contentGroup.querySelectorAll('rect.cover-photo-slot, circle.cover-photo-slot')
      existingShapes.forEach(shape => shape.remove())
    }

    // Asegurar que los elementos de texto existan ANTES de renderizar el layout
    this.ensureTextElements(contentGroup)

    // Renderizar layout de fotos si existe
    if (cover.data) {
      this.renderCoverLayout(contentGroup, cover.data)
    }

    // Los elementos de texto se actualizarán por separado en updateTextPositions
  }

  ensureTextElements(contentGroup) {
    const svgWidth = this.pageWidth
    const svgHeight = this.pageHeight

    // Buscar elementos existentes por ID primero
    if (!this.leftCoverTitleText) {
      this.leftCoverTitleText = document.getElementById('cover-left-sheet-title-text')
    }
    if (!this.leftCoverSubtitleText) {
      this.leftCoverSubtitleText = document.getElementById('cover-left-sheet-subtitle-text')
    }

    // Crear el elemento de título si no existe
    if (!this.leftCoverTitleText) {
      const titleText = document.createElementNS('http://www.w3.org/2000/svg', 'text')
      titleText.setAttribute('id', 'cover-left-sheet-title-text')
      titleText.setAttribute('x', svgWidth * 0.5)
      titleText.setAttribute('y', svgHeight * 0.3)
      titleText.setAttribute('text-anchor', 'middle')
      titleText.setAttribute('dominant-baseline', 'middle')
      titleText.setAttribute('fill', '#FFF')
      titleText.setAttribute('font-weight', 'bold')
      titleText.setAttribute('font-family', 'Arial, sans-serif')
      titleText.textContent = 'Título de Portada'
      contentGroup.appendChild(titleText)
      this.leftCoverTitleText = titleText
    } else if (this.leftCoverTitleText.parentNode !== contentGroup) {
      // Si el texto existe pero no está en el grupo, moverlo
      contentGroup.appendChild(this.leftCoverTitleText)
    }

    // Crear el elemento de subtítulo si no existe
    if (!this.leftCoverSubtitleText) {
      const subtitleText = document.createElementNS('http://www.w3.org/2000/svg', 'text')
      subtitleText.setAttribute('id', 'cover-left-sheet-subtitle-text')
      subtitleText.setAttribute('x', svgWidth * 0.5)
      subtitleText.setAttribute('y', svgHeight * 0.4)
      subtitleText.setAttribute('text-anchor', 'middle')
      subtitleText.setAttribute('dominant-baseline', 'middle')
      subtitleText.setAttribute('fill', '#374151')
      subtitleText.setAttribute('font-family', 'Arial, sans-serif')
      subtitleText.textContent = 'Subtítulo'
      contentGroup.appendChild(subtitleText)
      this.leftCoverSubtitleText = subtitleText
    } else if (this.leftCoverSubtitleText.parentNode !== contentGroup) {
      // Si el texto existe pero no está en el grupo, moverlo
      contentGroup.appendChild(this.leftCoverSubtitleText)
    }
  }

  renderCoverLayout(contentGroup, data) {
    if (!data || !this.svgCoverDetails) return

    const svgWidth = this.pageWidth
    const svgHeight = this.pageHeight
    const shapes = data.split('|')

    shapes.forEach((shapeData, index) => {
      const shapeMatch = shapeData.trim().match(/^([rc]):([\d.]+),([\d.]+),([\d.]+),([\d.]+)$/)
      if (!shapeMatch) return

      const [, type, width, height, x, y] = shapeMatch
      const shapeElement = type === 'r' 
        ? document.createElementNS('http://www.w3.org/2000/svg', 'rect')
        : document.createElementNS('http://www.w3.org/2000/svg', 'circle')

      if (type === 'r') {
        shapeElement.setAttribute('x', parseFloat(x) * svgWidth)
        shapeElement.setAttribute('y', parseFloat(y) * svgHeight)
        shapeElement.setAttribute('width', parseFloat(width) * svgWidth)
        shapeElement.setAttribute('height', parseFloat(height) * svgHeight)
      } else {
        const radius = (parseFloat(width) * svgWidth) / 2
        shapeElement.setAttribute('r', radius)
        shapeElement.setAttribute('cx', parseFloat(x) * svgWidth + radius)
        shapeElement.setAttribute('cy', parseFloat(y) * svgHeight + radius)
      }

      shapeElement.setAttribute('fill', 'none')
      shapeElement.setAttribute('stroke', '#55859a')
      shapeElement.setAttribute('stroke-width', '1')
      shapeElement.setAttribute('class', 'cover-photo-slot')
      shapeElement.setAttribute('data-slot-index', index)

      contentGroup.appendChild(shapeElement)
    })
  }

  updateTitlePreview(text) {
    if (this.leftCoverTitleText) {
      this.leftCoverTitleText.textContent = text || 'Título de Portada'
    }
    // La contraportada no tiene texto editable
  }

  updateSubtitlePreview(text) {
    if (this.leftCoverSubtitleText) {
      this.leftCoverSubtitleText.textContent = text || 'Subtítulo'
    }
    // La contraportada no tiene texto editable
  }

  updateSpinePreview(text) {
    if (this.spineText) {
      this.spineText.textContent = text || 'Texto de lomo'
    }
  }

  updateTextPositions(cover) {
    const svgWidth = this.pageWidth
    const svgHeight = this.pageHeight

    // Calcular posición X del título según alineación
    const titleX = this.calculateTextX(cover.title_align || 'center', svgWidth)
    const titleAnchor = this.getTextAnchor(cover.title_align || 'center')
    const titleY = (cover.title_y || 0.3) * svgHeight
    const titleSize = (cover.title_size || 0.08) * svgHeight

    // Calcular posición X del subtítulo según alineación
    const subtitleX = this.calculateTextX(cover.subtitle_align || 'center', svgWidth)
    const subtitleAnchor = this.getTextAnchor(cover.subtitle_align || 'center')
    const subtitleY = (cover.subtitle_y || 0.4) * svgHeight
    const subtitleSize = (cover.subtitle_size || 0.06) * svgHeight

    // Actualizar título izquierdo en #svg-cover-details
    if (this.leftCoverTitleText) {
      this.leftCoverTitleText.setAttribute('x', titleX)
      this.leftCoverTitleText.setAttribute('y', titleY)
      this.leftCoverTitleText.setAttribute('text-anchor', titleAnchor)
      this.leftCoverTitleText.setAttribute('font-size', titleSize)
    }

    // Actualizar subtítulo izquierdo en #svg-cover-details
    if (this.leftCoverSubtitleText) {
      this.leftCoverSubtitleText.setAttribute('x', subtitleX)
      this.leftCoverSubtitleText.setAttribute('y', subtitleY)
      this.leftCoverSubtitleText.setAttribute('text-anchor', subtitleAnchor)
      this.leftCoverSubtitleText.setAttribute('font-size', subtitleSize)
    }

    // La contraportada no tiene texto editable, solo el logo
    // Los elementos rightCoverTitleText y rightCoverSubtitleText no se usan
  }

  calculateTextX(align, svgWidth) {
    switch (align) {
      case 'left':
        return svgWidth * 0.1
      case 'right':
        return svgWidth * 0.9
      default: // center
        return svgWidth * 0.5
    }
  }

  getTextAnchor(align) {
    switch (align) {
      case 'left':
        return 'start'
      case 'right':
        return 'end'
      default: // center
        return 'middle'
    }
  }

  markActiveChip(color) {
    if (!this.paletteContainer) return
    this.paletteContainer.querySelectorAll('[data-color]').forEach(chip => {
      if (chip.getAttribute('data-color') === color) {
        chip.classList.add('active')
      } else {
        chip.classList.remove('active')
      }
    })
  }

  parseColors(value) {
    if (!value) return []
    return value.split(',')
      .map(c => c.trim())
      .filter(c => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(c))
  }

  parseFonts(value) {
    if (!value) return []
    return value.split(',')
      .map(f => f.trim())
      .filter(f => f.length > 0)
  }

  hexToRgbCss(hexColor) {
    const rgb = this.hexToRgb(hexColor)
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
  }

  hexToRgb(hexColor) {
    let r, g, b
    if (hexColor.length === 4) {
      r = parseInt(hexColor[1] + hexColor[1], 16)
      g = parseInt(hexColor[2] + hexColor[2], 16)
      b = parseInt(hexColor[3] + hexColor[3], 16)
    } else {
      r = parseInt(hexColor.slice(1, 3), 16)
      g = parseInt(hexColor.slice(3, 5), 16)
      b = parseInt(hexColor.slice(5, 7), 16)
    }
    return { r, g, b }
  }

  getContrastColor(hexColor) {
    const { r, g, b } = this.hexToRgb(hexColor)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.5 ? '#000000' : '#FFFFFF'
  }
}

